import { React, useCallback, useMemo } from 'react';
import { Table } from 'antd';
import PropTypes from 'prop-types';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import MaterialUnitsSelector from '../../common/inputs/MaterialUnitsSelector';
import {
  CATEGORIES,
  costCodeCategoryMap,
  categoriesWithHours,
  categoriesWithDays,
} from '../projectHelpers';
import { currencyFormatter, currencyParser } from '../../helpers/inputParsers';
import VerticallyCenteredNumberInput from '../../common/inputs/VerticallyCenteredNumberInput';
import { getMaterialCostColumns, getEquipmentCostColumns, getBasicCostColumns } from '../ProjectProgress/ProgressColumns';
import { calculateEquipmentCost, calculateEstimatedCostFromHours } from './budgetTrackingHelpers';
import { EQUIPMENT_CATEGORY_ID, MATERIAL_CATEGORY_ID } from '../../helpers/costcodeHelpers';

const costcodeCategoryKeys = new Set(['hours', 'days', 'estimatedCost', 'estimatedQuantity', 'units']);

function ProjectBudgetTrackingUnphased({
  isNotDisplay,
  onEditCostcodeEstimateChanged,
  unphasedCostcodes,
  categories,
  useBuildEstimate,
}) {
  const onValueChanged = useCallback(({
    record,
    key,
    categoryId,
  }) => (value) => {
    let valueToUse = value;

    // Handle NaN values that come from VerticallyCenteredNumberInput when field is cleared
    if (Number.isNaN(value)) {
      valueToUse = 0;
    }

    const updateObj = {
      categoryAssignments: {
        [categoryId]: {},
      },
    };

    if (key === 'addQuantities') {
      updateObj.addQuantities = true;
    } else if (costcodeCategoryKeys.has(key)) {
      updateObj.categoryAssignments[categoryId] = {
        [key]: valueToUse,
      };
    } else {
      updateObj[key] = valueToUse;
    }

    const isNotEquipment = categoryId !== categories?.Equipment?.id;

    if (!!useBuildEstimate && key === 'hours' && isNotEquipment) {
      const { hourlyWage = 0 } = record;
      const calculatedEstimatedCost = calculateEstimatedCostFromHours(
        hourlyWage,
        valueToUse,
      ) || 0;
      updateObj.categoryAssignments[categoryId].estimatedCost = calculatedEstimatedCost;
    }
    if (!!useBuildEstimate && (key === 'dailyWage' || key === 'hourlyWage' || key === 'hours' || key === 'days') && !isNotEquipment) { // is equipment
      const updatedCategoryAssignments = {
        ...record.categoryAssignments,
        [categoryId]: {
          ...record.categoryAssignments?.[categoryId],
          ...updateObj.categoryAssignments[categoryId],
        },
      };
      // Create updated record with new values for calculation
      const updatedRecord = {
        ...record,
        [key]: valueToUse,
        categoryAssignments: updatedCategoryAssignments,
      };

      const calculatedEstimatedCost = calculateEquipmentCost(
        updatedRecord,
        categories?.Equipment?.id,
      );

      updateObj.categoryAssignments[categoryId].estimatedCost = calculatedEstimatedCost;
    }
    onEditCostcodeEstimateChanged(record, updateObj);
  }, [onEditCostcodeEstimateChanged, useBuildEstimate]);

  const getUnphasedCols = useCallback((type) => {
    const inputOptions = {
      formatter: currencyFormatter,
      parser: currencyParser,
    };

    const categoryId = categories[type]?.id;

    const cols = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Code',
        dataIndex: 'code',
        key: 'code',
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
      },
      {
        title: 'Total',
        dataIndex: 'estimatedCost',
        key: 'estimatedCost',
        width: 100,
        render: (_, record) => {
          const { categoryAssignments } = record ?? {};
          const ourCategoryValues = categoryAssignments?.[categoryId] ?? {};
          const { estimatedCost } = ourCategoryValues ?? {};

          if (isNotDisplay) {
            return (
              <VerticallyCenteredNumberInput
                min={0}
                value={estimatedCost}
                onChange={onValueChanged({ record, key: 'estimatedCost', categoryId })}
                style={{ width: 150 }}
                formatter={inputOptions.formatter}
                parser={inputOptions.parser}
                asFloat
              />
            );
          }

          const ourNumber = !Number.isNaN(estimatedCost) ? estimatedCost : '';
          return ourNumber ? inputOptions.formatter(ourNumber) : ourNumber;
        },
      },
    ];
    if (categoriesWithHours.has(type)) {
      cols.push({
        title: 'Hours',
        dataIndex: 'hours',
        key: 'hours',
        width: 100,
        render: (_, record) => {
          const { categoryAssignments } = record ?? {};
          const ourCategoryValues = categoryAssignments?.[categoryId] ?? {};
          const { hours } = ourCategoryValues ?? {};
          if (isNotDisplay) {
            return (
              <VerticallyCenteredNumberInput
                min={0}
                value={hours}
                onChange={onValueChanged({ record, key: 'hours', categoryId })}
                style={{ width: 150 }}
                asFloat
              />
            );
          }

          return Number.isInteger(hours) ? hours : '-';
        },
      });
    }
    if (categoriesWithDays.has(type)) {
      cols.push({
        title: 'Days',
        dataIndex: 'days',
        key: 'days',
        width: 100,
        render: (_, record) => {
          const { categoryAssignments } = record ?? {};
          const ourCategoryValues = categoryAssignments?.[categoryId] ?? {};
          const { days } = ourCategoryValues ?? {};
          if (isNotDisplay) {
            return (
              <VerticallyCenteredNumberInput
                min={0}
                value={days}
                onChange={onValueChanged({ record, key: 'days', categoryId })}
                style={{ width: 150 }}
                asFloat
              />
            );
          }

          return Number.isInteger(days) ? days : '-';
        },
      });
    }

    // Add Daily Cost and Hourly Cost columns for Equipment
    if (type === 'Equipment') {
      cols.push({
        title: 'Daily Cost',
        dataIndex: 'dailyWage',
        key: 'dailyWage',
        width: 125,
        render: (dailyWage, record) => {
          if (isNotDisplay) {
            return (
              <VerticallyCenteredNumberInput
                min={0}
                value={dailyWage}
                onChange={onValueChanged({ record, key: 'dailyWage', categoryId })}
                style={{ width: 120 }}
                formatter={inputOptions.formatter}
                parser={inputOptions.parser}
                asFloat
              />
            );
          }
          return dailyWage ? inputOptions.formatter(dailyWage) : '-';
        },
      });

      cols.push({
        title: 'Hourly Cost',
        dataIndex: 'hourlyWage',
        key: 'hourlyWage',
        width: 125,
        render: (hourlyWage, record) => {
          if (isNotDisplay) {
            return (
              <VerticallyCenteredNumberInput
                min={0}
                value={hourlyWage}
                onChange={onValueChanged({ record, key: 'hourlyWage', categoryId })}
                style={{ width: 120 }}
                formatter={inputOptions.formatter}
                parser={inputOptions.parser}
                asFloat
              />
            );
          }
          return hourlyWage ? inputOptions.formatter(hourlyWage) : '-';
        },
      });
    }

    if (type === 'Material') {
      cols.push({
        title: 'Estimated Quantity',
        dataIndex: 'estimatedQuantity',
        key: 'estimatedQuantity',
        width: 150,
        render: (_, record) => {
          const { categoryAssignments } = record ?? {};
          const ourCategoryValues = categoryAssignments?.[categoryId] ?? {};
          const { estimatedQuantity } = ourCategoryValues ?? {};
          if (!isNotDisplay) return estimatedQuantity;
          if (!record.addQuantities && !estimatedQuantity) {
            return {
              children: (
                <BorderlessButton
                  title="Add Quantities"
                  style={{ width: 150 }}
                  onClick={onValueChanged({
                    record,
                    key: 'addQuantities',
                    categoryId,
                    newValue: true,
                  })}
                />
              ),
              props: { colSpan: 2 },
            };
          }
          return (
            <VerticallyCenteredNumberInput
              min={0}
              value={estimatedQuantity}
              onChange={onValueChanged({ record, key: 'estimatedQuantity', categoryId })}
              asFloat
            />
          );
        },
      });

      cols.push({
        title: 'Units',
        dataIndex: 'units',
        key: 'units',
        width: 150,
        render: (_, record) => {
          const { categoryAssignments } = record ?? {};
          const ourCategoryValues = categoryAssignments?.[categoryId] ?? {};
          const { estimatedQuantity, units } = ourCategoryValues ?? {};
          if (!record.addQuantities && !estimatedQuantity) return null;
          if (isNotDisplay) {
            return (
              <MaterialUnitsSelector
                value={units}
                onSelect={onValueChanged({ record, key: 'units', categoryId })}
              />
            );
          }
          return units;
        },
      });
    }
    return cols;
  }, [
    isNotDisplay,
    onValueChanged,
    categories,
  ]);

  const expandableCostRowRender = useCallback((record) => {
    const costcodeCostEstimates = record.costUpdates ?? [];
    let getColumns = null;

    if (record.categoryIds?.includes(MATERIAL_CATEGORY_ID)) {
      getColumns = getMaterialCostColumns;
    } else if (record.categoryIds?.includes(EQUIPMENT_CATEGORY_ID)) {
      getColumns = getEquipmentCostColumns;
    } else {
      getColumns = getBasicCostColumns;
    }

    return (
      <Table
        columns={getColumns()}
        dataSource={costcodeCostEstimates}
        pagination={false}
        size="small"
        style={{ marginBottom: 10 }}
        rowKey="id"
      />
    );
  }, []);

  const categoryMap = useMemo(() => costCodeCategoryMap(unphasedCostcodes), [unphasedCostcodes]);

  const getCategoryTitle = useCallback((categoryName) => () => `Unphased ${categoryName} Cost Codes`);

  const unphasedTables = useMemo(() => CATEGORIES.map((categoryName) => {
    const {
      [categoryName]: {
        id: categoryId,
      } = {},
    } = categories;
    const {
      [categoryId]: categoryCostCodes = [],
    } = categoryMap;
    return (
      <Table
        key={categoryName}
        title={getCategoryTitle(categoryName)}
        size="small"
        columns={getUnphasedCols(categoryName)}
        pagination={false}
        dataSource={categoryCostCodes}
        style={{ marginBottom: 50 }}
        rowKey="id"
        expandable={{
          defaultExpandAllRows: false,
          expandedRowRender: expandableCostRowRender,
          rowExpandable: (record) => record?.costUpdates?.length,
        }}
      />
    );
  }), [
    categoryMap,
    categories,
    getUnphasedCols,
    expandableCostRowRender,
  ]);

  return unphasedTables;
}

/* eslint-disable react/forbid-prop-types */
ProjectBudgetTrackingUnphased.propTypes = {
  isNotDisplay: PropTypes.bool.isRequired,
  onEditCostcodeEstimateChanged: PropTypes.func.isRequired,
  unphasedCostcodes: PropTypes.array.isRequired,
  categories: PropTypes.object.isRequired,
};

export default ProjectBudgetTrackingUnphased;
